import { apiClient } from "@/lib/api-client";

const API_BASE = "/api/v1/contractor-profile";

export type ContractorProfileTemplate = "template_basic";

export interface ContractorCompany {
	name: string;
	logoUrl?: string;
	description?: string;
	experienceYears?: number;
	serviceAreas: string[];
	specialisations: string[];
}

export interface ContractorTeamMember {
	id?: string;
	name: string;
	role: string;
	experienceYears?: number;
	shortBio?: string;
	profilePhotoUrl?: string;
}

export interface ContractorProfileProject {
	id?: string;
	title: string;
	shortDescription: string;
	thumbnailUrl: string;
	imageUrls: string[];
}

export interface ContractorPublishedProfile {
	id: string;
	template: ContractorProfileTemplate;
	isPublished: boolean;
	slug?: string;
}

export interface MyContractorProfileResponse {
	profile: ContractorPublishedProfile;
	company: ContractorCompany;
	teamMembers: ContractorTeamMember[];
	projects: ContractorProfileProject[];
	contractor: {
		id: string;
		name: string;
		profilePictureUrl?: string;
		isVerified: boolean;
		reviewStatus: "pending" | "approved" | "rejected";
		reviewNotes: string | null;
	};
}

export interface PublicContractorProfileResponse {
	profile: ContractorPublishedProfile;
	company: ContractorCompany;
	teamMembers: ContractorTeamMember[];
	contractor: {
		id: string;
		name: string;
		profilePictureUrl?: string;
		isVerified: boolean;
	};
	projects: Array<{
		id: string;
		title: string;
		shortDescription: string;
		thumbnailUrl: string;
	}>;
}

export interface PublicContractorProjectResponse {
	previewOnly: boolean;
	project: {
		id: string;
		title: string;
		shortDescription: string;
		thumbnailUrl: string;
		imageUrls?: string[];
	};
}

export async function getMyContractorProfile(): Promise<MyContractorProfileResponse | null> {
	try {
		const response = await apiClient.get(`${API_BASE}/me`);
		return response.data.data;
	} catch {
		return null;
	}
}

export async function saveMyContractorProfile(payload: {
	template: ContractorProfileTemplate;
	company: ContractorCompany;
	teamMembers: ContractorTeamMember[];
	projects: ContractorProfileProject[];
}): Promise<{
	success: boolean;
	profile?: ContractorPublishedProfile;
	projects?: ContractorProfileProject[];
	error?: string;
}> {
	try {
		const response = await apiClient.put(`${API_BASE}/me`, payload);
		return {
			success: true,
			profile: response.data.data.profile,
			projects: response.data.data.projects,
		};
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to save contractor profile",
		};
	}
}

export async function publishMyContractorProfile(): Promise<{
	success: boolean;
	shareUrlPath?: string;
	error?: string;
}> {
	try {
		const response = await apiClient.post(`${API_BASE}/me/publish`);
		return {
			success: true,
			shareUrlPath: response.data.data.shareUrlPath,
		};
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error:
				err.response?.data?.message || "Failed to publish contractor profile",
		};
	}
}

export async function unpublishMyContractorProfile(): Promise<{
	success: boolean;
	error?: string;
}> {
	try {
		await apiClient.post(`${API_BASE}/me/unpublish`);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error:
				err.response?.data?.message || "Failed to unpublish contractor profile",
		};
	}
}

export async function getPublicContractorProfile(
	slugOrToken: string,
): Promise<PublicContractorProfileResponse | null> {
	try {
		const response = await apiClient.get(`${API_BASE}/${slugOrToken}`);
		return response.data.data;
	} catch {
		return null;
	}
}

export async function getPublicContractorProfileProject(
	slugOrToken: string,
	projectId: string,
): Promise<PublicContractorProjectResponse | null> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/${slugOrToken}/projects/${projectId}`,
		);
		return response.data.data;
	} catch {
		return null;
	}
}
